import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import RestrictionBadge from "../components/RestrictionBadge";
import MapView from "../components/MapView";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { MapPin, Globe, Phone, Star, MessageSquareHeart, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function PlaceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/places/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="max-w-7xl mx-auto p-10">Carregando...</div>;
  const { place, reviews } = data;

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Faça login para avaliar"); return; }
    if (!comment.trim()) { toast.error("Escreva um comentário"); return; }
    setSubmitting(true);
    try {
      await api.post(`/places/${id}/reviews`, { rating, comment });
      toast.success("Avaliação enviada! Aguardando moderação.");
      setComment(""); setRating(5);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const img = place.photo_path ? fileUrl(place.photo_path) : null;

  return (
    <div data-testid="place-detail-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {place.status !== "approved" && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 p-4 mb-6 text-sm font-bold">
          ⏳ Este lugar está com status &quot;{place.status}&quot; e ainda não foi aprovado pela moderação.
        </div>
      )}

      <div className="grid md:grid-cols-5 gap-8">
        <div className="md:col-span-3">
          <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-brand-bg border border-brand-border">
            {img ? <img src={img} alt={place.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-brand-textMuted">Sem foto</div>}
          </div>
        </div>
        <div className="md:col-span-2 flex flex-col">
          <div className="flex flex-wrap gap-2 mb-3">
            {(place.restrictions || []).map((r) => <RestrictionBadge key={r} type={r} size="lg" />)}
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl text-brand-text leading-tight">{place.name}</h1>
          <div className="mt-3 flex items-center gap-2 text-brand-textMuted">
            <MapPin className="w-4 h-4" /> {place.address}, {place.city} · {place.state}
          </div>
          {avg && (
            <div className="mt-3 inline-flex items-center gap-1.5 font-bold text-brand-text">
              <Star className="w-5 h-5 fill-brand-yellow text-brand-yellow" /> {avg} <span className="text-brand-textMuted text-sm font-normal">({reviews.length})</span>
            </div>
          )}
          <p className="mt-5 text-brand-text/90 leading-relaxed">{place.description}</p>
          <div className="mt-6 space-y-2 text-sm">
            {place.website && <a href={place.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand-primary font-bold hover:underline"><Globe className="w-4 h-4" /> {place.website}</a>}
            {place.phone && <span className="flex items-center gap-2 text-brand-text font-bold"><Phone className="w-4 h-4" /> {place.phone}</span>}
          </div>
          <div className="mt-6 inline-flex items-center gap-2 text-xs text-brand-textMuted">
            <ShieldCheck className="w-4 h-4 text-brand-primary" /> Aprovado por moderador
          </div>
        </div>
      </div>

      {(place.latitude && place.longitude) && (
        <div className="mt-12">
          <h2 className="font-heading font-bold text-2xl text-brand-text mb-4">Localização</h2>
          <MapView places={[place]} height="380px" />
        </div>
      )}

      {/* Reviews */}
      <div className="mt-14">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-brand-text inline-flex items-center gap-2">
            <MessageSquareHeart className="w-7 h-7 text-brand-secondary" /> Opiniões da comunidade
          </h2>
        </div>

        {/* Review form */}
        {user ? (
          <form onSubmit={submitReview} className="rounded-3xl bg-white border border-brand-border p-6 mb-8" data-testid="review-form">
            <div className="flex items-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`star-${n}`} onClick={() => setRating(n)}>
                  <Star className={`w-7 h-7 ${n <= rating ? "fill-brand-yellow text-brand-yellow" : "text-brand-border"}`} />
                </button>
              ))}
              <span className="ml-2 text-sm font-bold text-brand-textMuted">{rating}/5</span>
            </div>
            <Textarea
              data-testid="review-textarea"
              placeholder="Conte sua experiência..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              className="bg-brand-bg border-brand-border rounded-2xl"
            />
            <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-brand-textMuted">Sua avaliação será publicada após aprovação do moderador.</p>
              <Button data-testid="submit-review-btn" disabled={submitting} className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">
                {submitting ? "Enviando..." : "Enviar avaliação"}
              </Button>
            </div>
          </form>
        ) : (
          <div className="rounded-3xl bg-white border border-brand-border p-6 mb-8 text-center">
            <p className="text-brand-textMuted">
              <Link to="/login" className="text-brand-primary font-bold hover:underline">Entre</Link> para avaliar este lugar.
            </p>
          </div>
        )}

        {reviews.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-brand-border p-10 text-center text-brand-textMuted">Seja a primeira pessoa a avaliar este lugar.</div>
        ) : (
          <div className="grid gap-4">
            {reviews.map((r) => (
              <div key={r.review_id} data-testid={`review-${r.review_id}`} className="rounded-3xl bg-white border border-brand-border p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {r.user_picture
                      ? <img src={r.user_picture} alt={r.user_name} className="w-10 h-10 rounded-full object-cover" />
                      : <span className="w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center font-bold">{(r.user_name || "?")[0]}</span>}
                    <div>
                      <div className="font-bold text-brand-text">{r.user_name || "Anônimo"}</div>
                      <div className="text-xs text-brand-textMuted">{new Date(r.created_at).toLocaleDateString("pt-BR")}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={`star-${n}`} className={`w-4 h-4 ${n <= r.rating ? "fill-brand-yellow text-brand-yellow" : "text-brand-border"}`} />
                    ))}
                  </div>
                </div>
                <p className="mt-4 text-brand-text/90 leading-relaxed">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
